import React, { Component } from 'react';
import SideMenu from '../admin/SideMenu';
import TopMenu from '../admin/TopBar';
import { appDatabasePrimaryFunctions, base } from '../../base';
import '../../styles/css/AdminMain.css';
import BasicDropzone from "../utils/Dropzone";
import { ErrorMessage, Toast } from "../utils/HelpfulFunction";
import { moduleRefreshWarning } from "../utils/moduleRefreshMessage";
import UploadImage from "../utils/UploadImage";
import { experiencePath, getScope, subscribeToScope, registerScopeGuard } from '../utils/experienceScope';

// Check if HockeyTech is configured via environment variables
const isHockeyTechConfigured = !!(
    process.env.REACT_APP_HOCKEYTECH_API_KEY &&
    process.env.REACT_APP_HOCKEYTECH_CLIENT_CODE
);

// API URL for Firebase Functions
let apiUrl = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/api/`;
if (process.env.NODE_ENV === "development") {
    apiUrl = `http://localhost:5001/${process.env.REACT_APP_FIREBASE_PROJECT_ID}/us-central1/api/`;
}

class SetUpSettings extends Component {
    constructor(props) {
        super(props);
        this.UploadImage = new UploadImage();
        this.state = {
            hideScoreboard: false,
            hideLoginFlow: false,
            loading: true,
            imageUrlPreview: {},
            settings: {},
            token: null,
            refreshingData: false,
            homeButtons: [],
            bottomTabs: [],
            sideMenuButtons: [],
            // The working scope, owned by utils/experienceScope: a ?experience=
            // in the URL wins, else this admin's stored choice, else '' for the
            // default experience. The top-bar switcher drives it.
            selectedExperience: getScope()
        };
        this.handleChange = this.handleChange.bind(this);
        this.handleModuleChange = this.handleModuleChange.bind(this);
        this.handleHomeVenueChange = this.handleHomeVenueChange.bind(this);
        this.handleBottomTabsChange = this.handleBottomTabsChange.bind(this);
        this.onDrop = this.onDrop.bind(this);
        this.refreshAllData = this.refreshAllData.bind(this);
        this.bindData = this.bindData.bind(this);
        this.handleExperienceChange = this.handleExperienceChange.bind(this);
    }

    async componentDidMount() {
        this.unsubscribeScope = subscribeToScope(id => this.handleExperienceChange(id));
        await this.bindData();

        // Get auth token for API calls
        if (this.props.userData) {
            const token = await this.props.userData.getIdToken();
            this.setState({ token, loading: false });
        } else {
            this.setState({ loading: false });
        }
    }

    async bindData() {
        const { selectedExperience } = this.state;
        this.settingsRef = base.syncState(experiencePath(selectedExperience, 'settings'), {
            context: this,
            state: 'settings',
        });

        // Fetch button configurations to check for module usage
        try {
            const [homeButtonsSnap, bottomTabsSnap, sideMenuSnap] = await Promise.all([
                appDatabasePrimaryFunctions.ref(experiencePath(selectedExperience, 'homeButtonsBuilt')).once('value'),
                appDatabasePrimaryFunctions.ref(experiencePath(selectedExperience, 'settings/bottomTabs/tabs')).once('value'),
                appDatabasePrimaryFunctions.ref(experiencePath(selectedExperience, 'sideMenuOptions')).once('value')
            ]);

            const homeButtons = homeButtonsSnap.val() ? Object.values(homeButtonsSnap.val()) : [];
            const bottomTabs = bottomTabsSnap.val() ? Object.values(bottomTabsSnap.val()) : [];
            const sideMenuButtons = sideMenuSnap.val() ? Object.values(sideMenuSnap.val()) : [];

            this.setState({ homeButtons, bottomTabs, sideMenuButtons, loading: false });
        } catch (e) {
            console.error('Error fetching button configurations:', e);
            this.setState({ loading: false });
        }
    }

    handleExperienceChange(experienceId) {
        // Detach the current experience's binding before re-binding to the new one
        base.removeBinding(this.settingsRef);
        this.setState({
            selectedExperience: experienceId,
            settings: {},
            homeButtons: [],
            bottomTabs: [],
            sideMenuButtons: [],
            loading: true
        }, () => this.bindData());
    }

    componentWillUnmount() {
        if (this.unsubscribeScope) this.unsubscribeScope();
        base.removeBinding(this.settingsRef);
    }

    onDrop(files, rejected, fieldName) {
        if (rejected.length > 0) {
            ErrorMessage.fire({
                title: 'Image cannot be uploaded',
                text: 'Make sure the image is less than 2mbs and it is an accepted file type'
            });
            return;
        }
        this.setState({ loading: true });

        this.UploadImage.upload_file(files[0]).then(res => {
            this.setState({ loading: false });
            if (res.error) {
                ErrorMessage.fire({
                    title: 'Image cannot be uploaded',
                    text: res.error,
                });
            } else if (res.imageUrl) {
                this.setState({
                    settings: {
                        ...this.state.settings,
                        [fieldName]: res.imageUrl
                    },
                    imageUrlPreview: {
                        ...this.state.imageUrlPreview,
                        [fieldName]: {
                            file: files[0],
                            preview: URL.createObjectURL(files[0])
                        }
                    }
                });
            } else {
                ErrorMessage.fire({
                    title: 'Image cannot be uploaded',
                    text: 'Something went wrong, please re-upload your image and try again!'
                });
            }
        });
    }

    handleChange(evt) {
        const { name, type, checked, value } = evt.target;
        const settings = { ...this.state.settings, [name]: type === 'checkbox' ? checked : value };
        this.setState({ settings });
    }

    getModuleUsage(moduleName) {
        const { homeButtons, bottomTabs, sideMenuButtons, settings } = this.state;
        const usages = [];

        // Define what buttonTypes to check for each module
        const moduleChecks = {
            'leagueScores': { buttonTypes: ['isLeagueScoresModule'] },
            'seasonGames': { buttonTypes: ['isSeasonGamesModule'] },
            'schedule': { buttonTypes: ['isScheduleModule'], screen: 'Schedule' },
            'roster': { buttonTypes: ['isRosterModule'], screen: 'Roster' },
            'standings': { buttonTypes: ['isStandingsModule'], screen: 'Stats', checkOther: 'playerLeaders' },
            'playerLeaders': { buttonTypes: ['isPlayerLeadersModule'], screen: 'Stats', checkOther: 'standings' },
            'playerPick': { buttonTypes: ['isPlayerPick'] }
        };

        const check = moduleChecks[moduleName];
        if (!check) return usages;

        // For Stats screen, only block if the OTHER module (standings/playerLeaders) is also disabled
        if (check.checkOther && check.screen) {
            const otherEnabled = settings?.modules?.[check.checkOther]?.enabled;
            if (otherEnabled) {
                // The other module is still enabled, so Stats screen will still work
                // But still check for direct module buttonTypes
                check.screen = null;
            }
        }

        // Check Home Buttons
        homeButtons.forEach(btn => {
            // Check for module-specific buttonTypes (e.g., isLeagueScoresModule, isScheduleModule)
            if (check.buttonTypes && check.buttonTypes.includes(btn.buttonType)) {
                usages.push(`Home Button: "${btn.header || btn.label || 'Unnamed'}"`);
            }
            // Check for isScreen with matching screen destination
            if (check.screen && btn.buttonType === 'isScreen' && btn.screen === check.screen) {
                usages.push(`Home Button: "${btn.header || btn.label || 'Unnamed'}"`);
            }
        });

        // Check Bottom Tabs
        bottomTabs.forEach(tab => {
            if (tab.visible === false) return; // Skip hidden tabs
            // Check for module-specific buttonTypes
            if (check.buttonTypes && check.buttonTypes.includes(tab.buttonType)) {
                usages.push(`Bottom Tab: "${tab.label || 'Unnamed'}"`);
            }
            // Check for isScreen with matching screen destination
            if (check.screen && tab.buttonType === 'isScreen' && tab.screen === check.screen) {
                usages.push(`Bottom Tab: "${tab.label || 'Unnamed'}"`);
            }
        });

        // Check Side Menu Buttons
        sideMenuButtons.forEach(btn => {
            if (check.buttonTypes && check.buttonTypes.includes(btn.buttonType)) {
                usages.push(`Side Menu: "${btn.header || 'Unnamed'}"`);
            }
        });

        return usages;
    }

    async handleModuleChange(evt) {
        const { name, checked } = evt.target;
        // Map input names to module paths
        const moduleMap = {
            'leagueScoresEnabled': ['leagueScores', 'enabled'],
            'seasonGamesEnabled': ['seasonGames', 'enabled'],
            'playerPickEnabled': ['playerPick', 'enabled'],
            'standingsEnabled': ['standings', 'enabled'],
            'playerLeadersEnabled': ['playerLeaders', 'enabled'],
            'scheduleEnabled': ['schedule', 'enabled'],
            'rosterEnabled': ['roster', 'enabled']
        };

        const modulePath = moduleMap[name];
        if (modulePath) {
            const [moduleName, property] = modulePath;

            // If trying to disable, check if module is in use
            if (!checked) {
                const usages = this.getModuleUsage(moduleName);
                if (usages.length > 0) {
                    ErrorMessage.fire({
                        title: 'Cannot Disable Module',
                        html: `This module is currently in use by:<br><br><ul style="text-align: left; margin-left: 20px;">${usages.map(u => `<li>${u}</li>`).join('')}</ul><br>Please remove these items first before disabling the module.`
                    });
                    return;
                }
            }

            const settings = {
                ...this.state.settings,
                modules: {
                    ...this.state.settings.modules,
                    [moduleName]: {
                        ...this.state.settings.modules?.[moduleName],
                        [property]: checked
                    }
                }
            };
            this.setState({ settings });

            // If enabling a module, fetch initial data via API. The refresh
            // endpoint only populates the default team's root data, so skip it
            // when editing another experience (its data comes from the poller).
            if (checked && this.state.token && !this.state.selectedExperience) {
                try {
                    const response = await fetch(apiUrl + "refreshModule", {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${this.state.token}`
                        },
                        body: JSON.stringify({ moduleName })
                    });
                    const data = await response.json();
                    if (response.ok && data.success) {
                        Toast.fire({ icon: 'success', title: `${moduleName} data loaded` });
                    } else if (data.error) {
                        console.warn(`Module refresh warning: ${data.error}`);
                        Toast.fire({ icon: 'warning', title: moduleRefreshWarning(response.status, data.error) });
                    }
                } catch (error) {
                    // Silently fail if functions emulator isn't running or API unavailable
                    // Module toggle still works, just won't pre-fetch data
                    if (process.env.NODE_ENV !== 'development') {
                        console.error('Error refreshing module:', error);
                    }
                }
            }
        }
    }

    handleHomeVenueChange(evt) {
        const { name, value } = evt.target;
        const settings = {
            ...this.state.settings,
            homeVenue: {
                ...this.state.settings.homeVenue,
                [name]: value
            }
        };
        this.setState({ settings });
    }

    handleBottomTabsChange(evt) {
        const { name, type, checked, value } = evt.target;
        const settings = {
            ...this.state.settings,
            bottomTabs: {
                ...this.state.settings.bottomTabs,
                [name]: type === 'checkbox' ? checked : value
            }
        };
        this.setState({ settings });
    }

    async refreshAllData() {
        const { settings, token } = this.state;
        if (!token) {
            Toast.fire({ icon: 'error', title: 'Not authenticated' });
            return;
        }

        const modules = settings.modules || {};
        const enabledModules = Object.entries(modules)
            .filter(([_, config]) => config?.enabled)
            .map(([name]) => name);

        if (enabledModules.length === 0) {
            Toast.fire({ icon: 'warning', title: 'No modules enabled' });
            return;
        }

        this.setState({ refreshingData: true });
        let successCount = 0;
        let errorCount = 0;

        for (const moduleName of enabledModules) {
            try {
                const response = await fetch(apiUrl + "refreshModule", {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ moduleName })
                });
                const data = await response.json();
                if (response.ok && data.success) {
                    successCount++;
                } else {
                    errorCount++;
                }
            } catch (error) {
                console.error(`Error refreshing ${moduleName}:`, error);
                errorCount++;
            }
        }

        this.setState({ refreshingData: false });
        if (errorCount === 0) {
            Toast.fire({ icon: 'success', title: `Refreshed ${successCount} modules` });
        } else {
            Toast.fire({ icon: 'warning', title: `Refreshed ${successCount}, failed ${errorCount}` });
        }
    }

    renderImageUpload(label, fieldName) {
        const preview = this.state.imageUrlPreview[fieldName]?.preview;
        const settings = this.state.settings || {};
        const savedVersion = settings[fieldName]

        return (
            <div className="form-group" align="center">
                <label htmlFor={fieldName} style={{ width: '100%' }}>
                    {label}
                    {(preview || savedVersion) &&
                        <button
                            type="button"
                            className="admin-clear-image"
                            aria-label="Remove image"
                            onClick={() => this.setState({
                                settings: {
                                    ...this.state.settings,
                                    [fieldName]: null
                                },
                                imageUrlPreview: {
                                    ...this.state.imageUrlPreview,
                                    [fieldName]: {}
                                }
                            })}
                        >Remove</button>
                    }
                </label>
                <img
                    src={preview || savedVersion}
                    width="auto"
                    height="100px"
                    style={{ display: preview || savedVersion ? '' : 'none' }}
                    alt=""
                />
                <BasicDropzone
                    onDrop={(acceptedFiles, fileRejections) => this.onDrop(acceptedFiles, fileRejections, fieldName)}
                    preview={preview}
                />
            </div>
        );
    }

    render() {
        const { settings } = this.state;
        return (
            <div className="admin-wrapper">
                <div className="loading-screen" style={{ display: this.state.loading ? 'block' : 'none' }} />
                <SideMenu />
                <TopMenu />
                <div className="admin-main-panel">
                    <p className="admin-page-eyebrow">Access</p>
                    <p className="admin-header-text">Settings</p>
                    <div className="admin-form-box">
                        <div className="form-group admin-toggle-field">
                            <label className="col-form-label" htmlFor="hideScoreboard">Hide Scoreboard</label>
                            <label className="switch">
                                <input
                                    name="hideScoreboard"
                                    id="hideScoreboard"
                                    type="checkbox"
                                    checked={settings.hideScoreboard || false}
                                    onChange={this.handleChange}
                                />
                                <span className="slider round" />
                            </label>
                            <p className="admin-hint">Hides it everywhere, including a Scoreboard placed on the Home screen.</p>
                        </div>
                        <div className="form-group admin-toggle-field">
                            <label className="col-form-label" htmlFor="hideLoginFlow">Hide Login Flow</label>
                            <label className="switch">
                                <input
                                    name="hideLoginFlow"
                                    id="hideLoginFlow"
                                    type="checkbox"
                                    checked={settings.hideLoginFlow || false}
                                    onChange={this.handleChange}
                                />
                                <span className="slider round" />
                            </label>
                        </div>

                        <div style={{ marginTop: 30, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <p className="admin-header-text" style={{ fontSize: 18, margin: 0 }}>Modules</p>
                            {/* The refresh endpoint only refreshes the default team's root data,
                                so hide the button while editing another experience */}
                            {this.state.selectedExperience === '' && Object.values(settings.modules || {}).some(m => m?.enabled) && (
                                <button
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={this.refreshAllData}
                                    disabled={this.state.refreshingData}
                                    style={{ fontSize: 12 }}
                                >
                                    {this.state.refreshingData ? 'Refreshing...' : 'Refresh All Data'}
                                </button>
                            )}
                        </div>
                        <p style={{ fontSize: 12, color: '#666', marginBottom: 15 }}>
                            Enable modules to make them available as button types in the Home Screen setup.
                        </p>

                        {isHockeyTechConfigured && (
                            <div className="form-group admin-toggle-field">
                                <label className="col-form-label" htmlFor="leagueScoresEnabled">
                                    League Scores Module
                                    <span className="admin-toggle-desc">
                                        Shows a carousel of scores from around the league
                                    </span>
                                </label>
                                <label className="switch">
                                    <input
                                        name="leagueScoresEnabled"
                                        id="leagueScoresEnabled"
                                        type="checkbox"
                                        checked={settings.modules?.leagueScores?.enabled || false}
                                        onChange={this.handleModuleChange}
                                    />
                                    <span className="slider round" />
                                </label>
                                {settings.modules?.leagueScores?.enabled && (
                                    <div className="admin-toggle-sub">
                                        <label className="col-form-label" htmlFor="leagueScoresAutoPlaySeconds" style={{ fontSize: 13 }}>
                                            Auto-Advance Seconds
                                            <span className="admin-toggle-desc" style={{ fontSize: 11 }}>
                                                Set to 0 for manual-only scrolling
                                            </span>
                                        </label>
                                        <br />
                                        <input
                                            value={settings.modules?.leagueScores?.autoPlaySeconds ?? 5}
                                            name="leagueScoresAutoPlaySeconds"
                                            id="leagueScoresAutoPlaySeconds"
                                            type="number"
                                            min={0}
                                            max={60}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value, 10) || 0;
                                                this.setState({
                                                    settings: {
                                                        ...this.state.settings,
                                                        modules: {
                                                            ...this.state.settings.modules,
                                                            leagueScores: {
                                                                ...this.state.settings.modules?.leagueScores,
                                                                autoPlaySeconds: value
                                                            }
                                                        }
                                                    }
                                                });
                                            }}
                                            style={{ width: 80 }}
                                        />
                                        <span style={{ marginLeft: 10, color: '#666', fontSize: 11 }}>
                                            {(settings.modules?.leagueScores?.autoPlaySeconds ?? 5) === 0
                                                ? '(Manual only)'
                                                : `(${settings.modules?.leagueScores?.autoPlaySeconds ?? 5}s)`}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}

                        {isHockeyTechConfigured && (
                            <div className="form-group admin-toggle-field">
                                <label className="col-form-label" htmlFor="seasonGamesEnabled">
                                    Season Games Module
                                    <span className="admin-toggle-desc">
                                        Swipeable carousel of team's season games (no auto-advance)
                                    </span>
                                </label>
                                <label className="switch">
                                    <input
                                        name="seasonGamesEnabled"
                                        id="seasonGamesEnabled"
                                        type="checkbox"
                                        checked={settings.modules?.seasonGames?.enabled || false}
                                        onChange={this.handleModuleChange}
                                    />
                                    <span className="slider round" />
                                </label>
                            </div>
                        )}

                        {process.env.REACT_APP_HAS_PLAYER_PICK === 'true' && (
                            <div className="form-group admin-toggle-field">
                                <label className="col-form-label" htmlFor="playerPickEnabled">
                                    Player Pick Module
                                    <span className="admin-toggle-desc">
                                        Live player stats, points system, and leaderboards during games
                                    </span>
                                </label>
                                <label className="switch">
                                    <input
                                        name="playerPickEnabled"
                                        id="playerPickEnabled"
                                        type="checkbox"
                                        checked={settings.modules?.playerPick?.enabled || false}
                                        onChange={this.handleModuleChange}
                                    />
                                    <span className="slider round" />
                                </label>
                            </div>
                        )}

                        {isHockeyTechConfigured && (
                            <>
                                <div className="form-group admin-toggle-field">
                                    <label className="col-form-label" htmlFor="standingsEnabled">
                                        Standings Module
                                        <span className="admin-toggle-desc">
                                            Displays league standings table with team records
                                        </span>
                                    </label>
                                    <label className="switch">
                                        <input
                                            name="standingsEnabled"
                                            id="standingsEnabled"
                                            type="checkbox"
                                            checked={settings.modules?.standings?.enabled || false}
                                            onChange={this.handleModuleChange}
                                        />
                                        <span className="slider round" />
                                    </label>
                                </div>

                                <div className="form-group admin-toggle-field">
                                    <label className="col-form-label" htmlFor="playerLeadersEnabled">
                                        Player Leaders Module
                                        <span className="admin-toggle-desc">
                                            Shows top scorers, goalies, and stat leaders
                                        </span>
                                    </label>
                                    <label className="switch">
                                        <input
                                            name="playerLeadersEnabled"
                                            id="playerLeadersEnabled"
                                            type="checkbox"
                                            checked={settings.modules?.playerLeaders?.enabled || false}
                                            onChange={this.handleModuleChange}
                                        />
                                        <span className="slider round" />
                                    </label>
                                </div>

                                <div className="form-group admin-toggle-field">
                                    <label className="col-form-label" htmlFor="scheduleEnabled">
                                        Schedule Module
                                        <span className="admin-toggle-desc">
                                            Shows upcoming games and recent results
                                        </span>
                                    </label>
                                    <label className="switch">
                                        <input
                                            name="scheduleEnabled"
                                            id="scheduleEnabled"
                                            type="checkbox"
                                            checked={settings.modules?.schedule?.enabled || false}
                                            onChange={this.handleModuleChange}
                                        />
                                        <span className="slider round" />
                                    </label>
                                </div>

                                <div className="form-group admin-toggle-field">
                                    <label className="col-form-label" htmlFor="rosterEnabled">
                                        Team Roster Module
                                        <span className="admin-toggle-desc">
                                            Displays team roster with player photos and stats
                                        </span>
                                    </label>
                                    <label className="switch">
                                        <input
                                            name="rosterEnabled"
                                            id="rosterEnabled"
                                            type="checkbox"
                                            checked={settings.modules?.roster?.enabled || false}
                                            onChange={this.handleModuleChange}
                                        />
                                        <span className="slider round" />
                                    </label>
                                </div>
                            </>
                        )}

                        <p className="admin-header-text" style={{ marginTop: 30, marginBottom: 10, fontSize: 18 }}>Bottom Tab Navigation</p>
                        <p style={{ fontSize: 12, color: '#666', marginBottom: 15 }}>
                            Configure the bottom navigation tabs that appear on iOS and Android. You can enable/disable tabs, change labels, icons, and reorder them.
                        </p>

                        <div className="form-group admin-toggle-field">
                            <label className="col-form-label" htmlFor="bottomTabsEnabled">
                                Enable Bottom Tabs
                                <span className="admin-toggle-desc">
                                    Shows navigation tabs at the bottom of the screen (iOS and Android)
                                </span>
                            </label>
                            <label className="switch">
                                <input
                                    name="enabled"
                                    id="bottomTabsEnabled"
                                    type="checkbox"
                                    checked={settings.bottomTabs?.enabled === true}
                                    onChange={this.handleBottomTabsChange}
                                />
                                <span className="slider round" />
                            </label>
                            {settings.bottomTabs?.enabled === true && (
                                <p style={{ fontSize: 12, color: '#666', marginTop: 10 }}>
                                    Configure tabs in the <a href="/bottomtabs" style={{ color: '#007bff' }}>Bottom Tabs</a> page.
                                </p>
                            )}
                        </div>

                        {/* Check-in needs these coordinates as much as player pick does —
                            the section's own copy says so. Gated on playerPick alone, a
                            check-in tenant had no way to set a home venue at all. */}
                        {(process.env.REACT_APP_HAS_PLAYER_PICK === 'true' || process.env.REACT_APP_HAS_CHECK_IN === 'true') && (
                            <>
                                <p className="admin-header-text" style={{ marginTop: 30, marginBottom: 10, fontSize: 18 }}>Home Venue</p>
                                <p style={{ fontSize: 12, color: '#666', marginBottom: 15 }}>
                                    Configure your home venue for automatic game setup and fan check-in functionality.
                                </p>

                                <div className="form-group">
                                    <label className="col-form-label" htmlFor="homeVenueName">Venue Name</label>
                                    <br />
                                    <input
                                        value={settings.homeVenue?.name || ''}
                                        name="name"
                                        id="homeVenueName"
                                        type="text"
                                        placeholder="e.g., Acrisure Arena"
                                        onChange={this.handleHomeVenueChange}
                                        style={{ width: '100%', maxWidth: 400 }}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="col-form-label" htmlFor="homeVenueAddress">Address</label>
                                    <br />
                                    <input
                                        value={settings.homeVenue?.address || ''}
                                        name="address"
                                        id="homeVenueAddress"
                                        type="text"
                                        placeholder="e.g., 75702 Varner Rd, Palm Desert, CA 92211"
                                        onChange={this.handleHomeVenueChange}
                                        style={{ width: '100%', maxWidth: 400 }}
                                    />
                                </div>

                                <div className="form-group" style={{ display: 'flex', gap: 20 }}>
                                    <div>
                                        <label className="col-form-label" htmlFor="homeVenueLat">Latitude</label>
                                        <br />
                                        <input
                                            value={settings.homeVenue?.lat || ''}
                                            name="lat"
                                            id="homeVenueLat"
                                            type="number"
                                            step="any"
                                            placeholder="e.g., 33.7372"
                                            onChange={this.handleHomeVenueChange}
                                            style={{ width: 150 }}
                                        />
                                    </div>
                                    <div>
                                        <label className="col-form-label" htmlFor="homeVenueLng">Longitude</label>
                                        <br />
                                        <input
                                            value={settings.homeVenue?.lng || ''}
                                            name="lng"
                                            id="homeVenueLng"
                                            type="number"
                                            step="any"
                                            placeholder="e.g., -116.3781"
                                            onChange={this.handleHomeVenueChange}
                                            style={{ width: 150 }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        <p className="admin-header-text" style={{ marginTop: 30, marginBottom: 10, fontSize: 18 }}>Theme</p>

                        <div className="form-group">
                            <label className="col-form-label" htmlFor="primaryColor">
                                Primary Color
                                <span className="admin-toggle-desc">
                                    Used for module headers, buttons, and accents
                                </span>
                            </label>
                            <br />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <input
                                    value={settings.primaryColor || '#4CAF50'}
                                    name="primaryColor"
                                    id="primaryColor"
                                    type="color"
                                    onChange={this.handleChange}
                                    style={{ width: 60, height: 40, padding: 0, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
                                />
                                <input
                                    value={settings.primaryColor || '#4CAF50'}
                                    name="primaryColor"
                                    type="text"
                                    onChange={this.handleChange}
                                    placeholder="#4CAF50"
                                    style={{ width: 100, fontFamily: 'monospace' }}
                                />
                                <span style={{
                                    display: 'inline-block',
                                    padding: '8px 16px',
                                    backgroundColor: settings.primaryColor || '#4CAF50',
                                    color: '#fff',
                                    borderRadius: 4,
                                    fontSize: 12,
                                    fontWeight: 'bold'
                                }}>
                                    Preview
                                </span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="col-form-label" htmlFor="secondaryColor">
                                Secondary Color (Optional)
                                <span className="admin-toggle-desc">
                                    Used for gradients and secondary elements
                                </span>
                            </label>
                            <br />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <input
                                    value={settings.secondaryColor || ''}
                                    name="secondaryColor"
                                    id="secondaryColor"
                                    type="color"
                                    onChange={this.handleChange}
                                    style={{ width: 60, height: 40, padding: 0, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
                                />
                                <input
                                    value={settings.secondaryColor || ''}
                                    name="secondaryColor"
                                    type="text"
                                    onChange={this.handleChange}
                                    placeholder="#333333"
                                    style={{ width: 100, fontFamily: 'monospace' }}
                                />
                            </div>
                        </div>

                        <p className="admin-header-text" style={{ marginTop: 30, marginBottom: 10, fontSize: 18 }}>General Settings</p>

                        <div className="form-group">
                            <label className="col-form-label" htmlFor="supportEmail">Support Email</label>
                            <br />
                            <input
                                value={this.state.settings.supportEmail}
                                name="supportEmail"
                                id="supportEmail"
                                type="email"
                                onChange={this.handleChange}
                            />
                        </div>
                        <div className="form-group">
                            <label className="col-form-label" htmlFor="minimumNumberOfSecondsSplashScreen">Minimum Number of Seconds of Splash Screen</label>
                            <br />
                            <input
                                value={this.state.settings.minimumNumberOfSecondsSplashScreen}
                                name="minimumNumberOfSecondsSplashScreen"
                                id="minimumNumberOfSecondsSplashScreen"
                                type="number"
                                min={1}
                                onChange={this.handleChange}
                            />
                        </div>
                        {this.renderImageUpload("Background Image (1125x2001)", "backgroundImage")}
                        {this.renderImageUpload("Top Bar Icon", "topBarIcon")}
                        {this.renderImageUpload("Scoreboard Home Team Logo", "scoreboardHomeTeamImage")}
                        {this.renderImageUpload("Menu Icon", "menuIcon")}
                        {this.renderImageUpload("Side Menu Top Image", "sideMenuTopImage")}
                        {this.renderImageUpload("Splash Screen", "splashScreen")}
                        {this.renderImageUpload("Auth Flow Image", "authFlowImage")}
                    </div>
                </div>
            </div>
        );
    }
}

export default SetUpSettings;
